import React, { useState, useEffect, useContext } from 'react';
import { List, Card, Tag, Typography, Timeline, Space, Button, Input, Select, DatePicker, Row, Col, Badge, Modal, message } from 'antd';
import { SearchOutlined, CalendarOutlined, ClockCircleOutlined, EyeOutlined, LikeOutlined, UndoOutlined } from '@ant-design/icons';
import { getAllPosts, getDeletedPosts, restorePost } from '../../api/mockData';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext'; // 引入 AuthContext
import ArchiveItem from '../../components/ArchiveItem';
import './Archive.css';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const Archive = () => {
  const { user } = useContext(AuthContext); // 获取当前登录用户
  const [archiveData, setArchiveData] = useState([]);
  const [deletedPosts, setDeletedPosts] = useState([]); // 新增：存储已删除的文章
  const [showDeleted, setShowDeleted] = useState(false); // 新增：控制是否显示已删除文章
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [dateRange, setDateRange] = useState(null);

  // 按年月归档文章数据
  useEffect(() => {
    // 获取所有文章（包括 localStorage 中的）
    const allPosts = getAllPosts();
    
    // 将文章按日期排序（从新到旧）
    const sortedPosts = [...allPosts].sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // 按年月分组
    const groupedPosts = {};
    sortedPosts.forEach(post => {
      const date = new Date(post.date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // 月份从0开始，需要+1
      const yearMonth = `${year}-${month.toString().padStart(2, '0')}`;
      
      if (!groupedPosts[yearMonth]) {
        groupedPosts[yearMonth] = [];
      }
      groupedPosts[yearMonth].push(post);
    });
    
    // 转换为时间线格式的数据 - 时间从新到旧排列
    const timelineData = Object.keys(groupedPosts)
      .sort((a, b) => b.localeCompare(a)) // 按时间倒序排列
      .map(key => {
        const [year, month] = key.split('-');
        // 每个月内的文章也按时间倒序排列（最新的在最前面）
        const sortedMonthPosts = groupedPosts[key].sort((a, b) => new Date(b.date) - new Date(a.date));
        return {
          year: parseInt(year),
          month: parseInt(month),
          posts: sortedMonthPosts
        };
      });
    
    setArchiveData(timelineData);
  }, []);

  // 获取已删除的文章
  useEffect(() => {
    const fetchDeletedPosts = async () => {
      try {
        const deleted = await getDeletedPosts();
        setDeletedPosts(deleted);
      } catch (error) {
        console.error('获取已删除文章失败:', error);
      }
    };

    fetchDeletedPosts();
  }, [showDeleted]); // 当显示状态改变时重新获取

  // 获取所有文章用于筛选
  const allPosts = getAllPosts();
  
  // 获取所有年份用于筛选
  const allYears = [...new Set(allPosts.map(post => new Date(post.date).getFullYear()))].sort((a, b) => b - a);
  
  // 获取所有标签用于筛选
  const allTags = [...new Set(allPosts.flatMap(post => post.tags || []))];

  // 计算总文章数和总阅读量
  const totalPosts = allPosts.length;
  const totalViews = allPosts.reduce((sum, post) => sum + (post.views || 0), 0);

  // 还原文章处理函数
  const handleRestore = async (postId) => {
    Modal.confirm({
      title: '确认还原',
      content: '确定要还原这篇文章吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          await restorePost(postId);
          // 刷新已删除文章列表
          const deleted = await getDeletedPosts();
          setDeletedPosts(deleted);
          message.success('文章还原成功！');
        } catch (error) {
          message.error('文章还原失败');
        }
      }
    });
  };

  // 过滤文章
  const filteredArchiveData = archiveData
    .filter(item => selectedYear === 'all' || item.year === parseInt(selectedYear))
    .map(item => ({
      ...item,
      posts: item.posts.filter(post => {
        const matchesSearch = 
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesMonth = selectedMonth === 'all' || item.month === parseInt(selectedMonth);
        
        const matchesTag = selectedTag === 'all' || post.tags.includes(selectedTag);
        
        // 检查日期范围
        let matchesDate = true;
        if (dateRange) {
          const postDate = new Date(post.date);
          const startDate = dateRange[0]?.toDate();
          const endDate = dateRange[1]?.toDate();
          
          if (startDate && endDate) {
            matchesDate = postDate >= startDate && postDate <= endDate;
          } else if (startDate) {
            matchesDate = postDate >= startDate;
          } else if (endDate) {
            matchesDate = postDate <= endDate;
          }
        }
        
        return matchesSearch && matchesMonth && matchesTag && matchesDate;
      })
    }))
    .filter(item => item.posts.length > 0); // 只显示包含匹配文章的月份

  // 检查是否为 qmau 用户
  const isQmauUser = user && user.name === 'qmau';

  return (
    <div className="archive-page">
      <Card className="archive-header-card">
        <div className="archive-title-section">
          <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>
            <CalendarOutlined style={{ marginRight: 12, color: '#1890ff' }} />
            文章归档
          </Title>
          <div className="archive-stats">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge count={totalPosts} overflowCount={9999} color="#1890ff" />
              <Text strong style={{ fontSize: '16px' }}>篇文章</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge count={totalViews} overflowCount={99999} color="#52c41a" />
              <Text strong style={{ fontSize: '16px' }}>次阅读</Text>
            </div>
          </div>
        </div>
      </Card>

      <Card className="archive-filters-card">
        <div className="archive-filters">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Search
                placeholder="搜索文章标题、内容或标签..."
                allowClear
                size="large"
                onSearch={setSearchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%' }}
                prefix={<SearchOutlined />}
              />
            </Col>
            
            <Col xs={24} sm={8} md={6} lg={4}>
              <Select
                placeholder="选择年份"
                allowClear
                style={{ width: '100%' }}
                size="large"
                onChange={setSelectedYear}
                options={[
                  { label: '全部年份', value: 'all' },
                  ...allYears.map(year => ({ label: `${year}年`, value: year.toString() }))
                ]}
              />
            </Col>
            
            <Col xs={24} sm={8} md={6} lg={4}>
              <Select
                placeholder="选择月份"
                allowClear
                style={{ width: '100%' }}
                size="large"
                onChange={setSelectedMonth}
                options={[
                  { label: '全部月份', value: 'all' },
                  ...Array.from({ length: 12 }, (_, i) => i + 1).map(month => ({
                    label: `${month}月`,
                    value: month.toString()
                  }))
                ]}
              />
            </Col>
            
            <Col xs={24} sm={8} md={6} lg={4}>
              <Select
                placeholder="选择标签"
                allowClear
                style={{ width: '100%' }}
                size="large"
                onChange={setSelectedTag}
                options={[
                  { label: '全部标签', value: 'all' },
                  ...allTags.map(tag => ({ label: tag, value: tag }))
                ]}
              />
            </Col>
            
            <Col xs={24} sm={12} md={12} lg={6}>
              <RangePicker
                style={{ width: '100%' }}
                size="large"
                placeholder={['开始日期', '结束日期']}
                onChange={setDateRange}
              />
            </Col>
            
            <Col xs={24} sm={12} md={12} lg={4}>
              <Button 
                type="default" 
                size="large" 
                block
                onClick={() => {
                  setSearchTerm('');
                  setSelectedYear('all');
                  setSelectedMonth('all');
                  setSelectedTag('all');
                  setDateRange(null);
                }}
              >
                重置
              </Button>
            </Col>
            
            {/* 仅对 qmau 用户显示查看已删按钮 */}
            {isQmauUser && (
              <Col xs={24} sm={12} md={12} lg={2}>
                <Button 
                  type={showDeleted ? "primary" : "default"} 
                  size="large" 
                  block
                  onClick={() => setShowDeleted(!showDeleted)}
                >
                  {showDeleted ? '隐藏已删' : '查看已删'}
                </Button>
              </Col>
            )}
          </Row>
        </div>
      </Card>

      {/* 显示已删除的文章 - 仅对 qmau 用户显示 */}
      {showDeleted && isQmauUser && (
        <Card className="deleted-posts-card" style={{ marginTop: 16 }}>
          <Title level={3} style={{ marginBottom: 16 }}>
            已删除的文章
          </Title>
          {deletedPosts.length > 0 ? (
            <List
              dataSource={deletedPosts}
              grid={{
                gutter: 16,
                xs: 1,
                sm: 1,
                md: 1,
                lg: 1,
                xl: 1,
                xxl: 1,
              }}
              renderItem={post => (
                <List.Item>
                  <Card 
                    hoverable
                    style={{ width: '100%' }}
                    actions={[
                      <Button 
                        type="link" 
                        icon={<UndoOutlined />} 
                        onClick={() => handleRestore(post.id)}
                      >
                        还原
                      </Button>
                    ]}
                  >
                    <Card.Meta
                      title={
                        <Link to={`/post/${post.id}`}>
                          {post.title}
                        </Link>
                      }
                      description={
                        <div>
                          <div style={{ marginBottom: 8 }}>
                            <Text type="secondary">
                              {new Date(post.date).toLocaleDateString('zh-CN')} • 
                              作者：{post.author} • 
                              阅读：{post.views || 0}
                            </Text>
                          </div>
                          <div>
                            {post.tags && post.tags.map((tag, index) => (
                              <Tag key={index} color="blue" style={{ margin: '2px' }}>{tag}</Tag>
                            ))}
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </List.Item>
              )}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text type="secondary">暂无已删除的文章</Text>
            </div>
          )}
        </Card>
      )}

      <Card className="archive-content-card">
        {filteredArchiveData.length > 0 ? (
          <div className="archive-timeline">
            <Timeline mode="left" pending="更多文章...">
              {filteredArchiveData.map((item, index) => (
                <Timeline.Item 
                  key={`${item.year}-${item.month}`} 
                  dot={
                    <div className="timeline-dot">
                      <ClockCircleOutlined />
                    </div>
                  }
                  style={{ padding: '16px 0' }}
                >
                  <div className="archive-month">
                    <Title level={4} style={{ margin: '0 0 16px 0' }}>
                      {item.year}年{item.month}月 
                      <Text type="secondary" style={{ marginLeft: 12, fontSize: '14px' }}>
                        ({item.posts.length}篇文章)
                      </Text>
                    </Title>
                    
                    <List
                      dataSource={item.posts}
                      grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 1,
                        md: 1,
                        lg: 1,
                        xl: 1,
                        xxl: 1,
                      }}
                      renderItem={post => (
                        <List.Item>
                          <ArchiveItem post={post} />
                        </List.Item>
                      )}
                    />
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </div>
        ) : (
          <div className="no-data-container">
            <div className="no-data-content">
              <CalendarOutlined style={{ fontSize: '48px', color: '#bfbfbf', marginBottom: 16 }} />
              <Title level={4} style={{ color: '#bfbfbf' }}>暂无匹配的文章</Title>
              <Text type="secondary">尝试调整筛选条件以获得更多结果</Text>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Archive;